import { isLiveMallClub } from '@/constants/systemType'
import classnames from 'classnames/bind'
import { useField, useFormikContext } from 'formik'
import { isEmpty, isNumber } from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

// Components
import Icon from '@/assets/icons'
import Button from '@/components/common/Button'
import ErrorMessage from './components/ErrorMessage'

// Constants
import { FIELD_BACKGROUND_COLOR } from '@/constants/fieldBackgroundColor'
import { FIELD_TYPE } from '@/constants/fieldType'
import { FIELD_VARIANT } from '@/constants/fieldVariant'

// Styles
import styles from './style.module.scss'

// PropTypes
const propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  titleClassName: PropTypes.string,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  fieldType: PropTypes.string,
  buttonConfig: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.object]),
  autoFocus: PropTypes.bool,
  isLight: PropTypes.bool,
  onClick: PropTypes.func,
  handleInputClearProps: PropTypes.func,
  isAutosuggest: PropTypes.bool,
  showError: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  variant: PropTypes.string,
  backgroundColor: PropTypes.string,
  navigationText: PropTypes.string,
  inputLengthLimit: PropTypes.object,
  showWordCount: PropTypes.bool,
  disabled: PropTypes.bool,
}

const defaultProps = {
  fieldType: FIELD_TYPE.INPUT,
  buttonConfig: {},
  autoFocus: false,
  isLight: false,
  onClick: () => {},
  handleInputClearProps: () => {},
  isAutosuggest: false,
  showError: false,
  showCloseButton: true,
  variant: FIELD_VARIANT.STANDARD,
  backgroundColor: FIELD_BACKGROUND_COLOR.LIGHT,
  disabled: false,
}

// Variables
const cx = classnames.bind(styles)

function FieldItem(props) {
  const {
    children,
    className,
    wrapperClassName,
    contentClassName,
    titleClassName,
    title,
    placeholder,
    name,
    fieldType,
    buttonConfig,
    error,
    autoFocus,
    isLight,
    onClick,
    handleInputClearProps,
    isAutosuggest,
    showError,
    showCloseButton,
    variant,
    backgroundColor,
    navigationText,
    inputLengthLimit,
    showWordCount,
    disabled,
  } = props

  const [isFieldFocus, setIsFieldFocus] = useState(autoFocus)

  const fieldItemRef = useRef(null)

  const [, meta] = useField(props)

  const { setFieldValue } = useFormikContext()

  const hasError = (meta.touched && !isEmpty(meta.error)) || !isEmpty(error)

  const isSelect = fieldType === FIELD_TYPE.SELECT // 因為 select 的行為不一樣，需要判斷 isSelect 另外做處理
  const isButton = fieldType === FIELD_TYPE.BUTTON
  const isInput = fieldType === FIELD_TYPE.INPUT
  const isDatepicker = fieldType === FIELD_TYPE.DATEPICKER

  const hasTitle = title?.length > 0
  const hasButton = !isEmpty(buttonConfig)

  const hasValue = !isEmpty(meta.value) || isNumber(meta.value)

  const focusOrWithValue = isFieldFocus || hasValue
  const focusWithoutValue = isFieldFocus && !hasValue

  const FieldItemChildren = React.Children.map(children, (child) => {
    const normalProps = { name }

    const inputProps = { ...normalProps, isFieldFocus, focusWithoutValue }
    const selectProps = { ...normalProps, fieldItemRef }
    const datepickerProps = { ...normalProps, fieldItemRef }

    let childProps = normalProps

    if (isInput) childProps = inputProps
    if (isSelect) childProps = selectProps
    if (isDatepicker) childProps = datepickerProps

    return React.cloneElement(child, childProps)
  })

  useEffect(() => {
    // 監聽 fieldItem focus
    const fieldItem = fieldItemRef.current

    const handleFieldItemFocus = () => {
      setIsFieldFocus(true)
    }

    const handleFieldItemBlur = () => {
      setIsFieldFocus(false)
    }

    fieldItem.addEventListener('focusin', handleFieldItemFocus)
    fieldItem.addEventListener('focusout', handleFieldItemBlur)

    return () => {
      fieldItem.removeEventListener('focusin', handleFieldItemFocus)
      fieldItem.removeEventListener('focusout', handleFieldItemBlur)
    }
  }, [])

  const handleInputClear = () => {
    setFieldValue(name, '')

    handleInputClearProps()
  }

  return (
    <div
      className={cx(
        'field-item',
        {
          'common-component-field-item': isLiveMallClub,
          'common-component-field-item--has-error': isLiveMallClub && hasError,
          'common-component-field-item--variant-standard': variant === FIELD_VARIANT.STANDARD,
          'common-component-field-item--variant-filled': variant === FIELD_VARIANT.FILLED,
          'common-component-field-item--variant-outlined': variant === FIELD_VARIANT.OUTLINED,
          'common-component-field-item--background-color-light': backgroundColor === FIELD_BACKGROUND_COLOR.LIGHT,
          'common-component-field-item--background-color-dark': backgroundColor === FIELD_BACKGROUND_COLOR.DARK,
        },
        className,
      )}
      data-is-button={isButton}
      onClick={() => {
        if (disabled) return
        onClick()
      }}
    >
      <div
        className={cx('field-item-wrapper', wrapperClassName)}
        ref={fieldItemRef}
        tabIndex={0}
        data-has-error={hasError}
        data-is-light={isLight}
        data-is-select={isSelect}
        data-is-datepicker={isDatepicker}
        data-variant={variant}
      >
        <div className={cx('field-item-wrapper-content', contentClassName)} data-has-title={hasTitle}>
          {title && (
            <p
              className={cx('field-item-wrapper-content-title', titleClassName)}
              data-is-focus={(!isSelect && isFieldFocus && !isButton) || (isAutosuggest && isFieldFocus)}
              data-has-value={hasValue}
              data-has-error={!focusOrWithValue && hasError}
              data-is-select={isSelect}
              data-is-button={isButton}
              data-variant={variant}
            >
              {title}
            </p>
          )}

          {placeholder && !hasValue && <div className={cx('field-item-wrapper-content-placeholder')}>{placeholder}</div>}
          {FieldItemChildren}
        </div>
        {hasButton && <Button {...buttonConfig}>{buttonConfig?.label}</Button>}
        {isButton && !disabled && (
          <div className={cx('field-item-wrapper-icon', 'arrow')}>
            <div className={cx('field-item-wrapper-icon-text')}>{navigationText}</div>
            <div className={cx('field-item-wrapper-icon', 'arrow-icon')}>
              <Icon.Arrow />
            </div>
          </div>
        )}
        {showWordCount && (
          <div className={cx('field-item-wrapper-word-count')}>
            {inputLengthLimit.value}/{inputLengthLimit.max}
          </div>
        )}
        {showCloseButton && isInput && hasValue && (
          <div className={cx('field-item-wrapper-icon', 'close')} onClick={handleInputClear}>
            <Icon.FieldClear />
          </div>
        )}
      </div>

      {showError && <ErrorMessage wrapperClassName={wrapperClassName} name={name} customError={error} />}
    </div>
  )
}

FieldItem.propTypes = propTypes
FieldItem.defaultProps = defaultProps

export default FieldItem
